import { useIntl } from "react-intl";

import { Chip } from "@/components/Chip/Chip";
import { IconButton } from "@/components/IconButton/IconButton";
import { CaretRightIcon } from "@/components/Icons/rtlReadyIcons";
import { Radio } from "@/components/Radio/Radio";
import {
  RadioGroup,
  type RadioGroupProps,
} from "@/components/Radio/RadioGroup";
import type { Traveller } from "@/features/travellers/types/types";
import { getTravellerAgeAtTravelDate } from "@/features/travellers/utils/ageValidation";
import { getTravelerTypeOfAge } from "@/features/travellers/utils/transformTraveler";
import { numericDateFormatOptions } from "@/utils/dates";
import { getAgeFromDateOfBirth } from "@/utils/getAgefromDateOfBirth";

type Props = RadioGroupProps & {
  travelDate: Date;
  selectedTravellers: Traveller[];
  onDetailsClick: (traveller: Traveller) => void;
};

export const MainTravellerRadioButtons = ({
  travelDate,
  selectedTravellers,
  onDetailsClick,
  ...props
}: Props) => {
  const intl = useIntl();

  return (
    <RadioGroup {...props} className="gap-4">
      {selectedTravellers.map((traveller) => {
        const ageAtTravelDate = getTravellerAgeAtTravelDate(
          traveller.birthDate,
          travelDate,
        );
        const ageGroupAtTravelDate = getTravelerTypeOfAge(ageAtTravelDate);

        const currentAge = getAgeFromDateOfBirth(traveller.birthDate);
        const currentAgeGroup = getTravelerTypeOfAge(currentAge);

        return (
          <div
            key={traveller.id}
            className="flex items-center justify-between gap-4"
          >
            <Radio
              value={traveller.id}
              disabled={ageGroupAtTravelDate !== "adult"}
              label={
                <div className="ms-2" data-sentry-mask>
                  <div className="mb-0.5 text-base text-text-primary">
                    {traveller.firstName} {traveller.lastName}
                  </div>

                  <div className="flex gap-1">
                    <Chip
                      color="neutral"
                      variant="outlined"
                      shape="rounded"
                      label={intl.formatDate(
                        traveller.birthDate,
                        numericDateFormatOptions,
                      )}
                    />

                    {props.value === traveller.id ? (
                      <Chip
                        color="accent-soft"
                        shape="rounded"
                        label={intl.formatMessage({
                          defaultMessage: "Main traveller",
                          description:
                            "Badge or label identifying the primary / main traveller in a booking",
                        })}
                      />
                    ) : null}
                  </div>

                  {ageGroupAtTravelDate !== currentAgeGroup ? (
                    <div className="mt-1 text-xs text-semantic-warning-strong">
                      {intl.formatMessage(
                        {
                          defaultMessage:
                            "Traveller will be {age} years old at travel date, which makes them counted as {ageGroupAtTravelDate, select, child {a Child} infant {an Infant} other {an Adult}}",
                          description:
                            "Error shown when a child traveller's age is not valid for the selected booking",
                        },
                        {
                          ageGroupAtTravelDate,
                          age: ageAtTravelDate,
                        },
                      )}
                    </div>
                  ) : null}
                </div>
              }
            />

            <div className="shrink-0 border-s border-stroke-neutral-soft px-1 py-0.5">
              <IconButton
                size="small"
                onClick={() => {
                  onDetailsClick(traveller);
                }}
                aria-label={intl.formatMessage({
                  defaultMessage: "Show traveller details",
                  description:
                    "Button to show detailed information about the traveller",
                })}
              >
                <CaretRightIcon
                  size={16}
                  color="var(--color-stone)"
                  weight="bold"
                />
              </IconButton>
            </div>
          </div>
        );
      })}
    </RadioGroup>
  );
};
